// The verifiable core of the eval system: score a candidate extraction against a
// human-labeled golden, field by field, grouped by pipeline stage. Generalizes the
// single-call `compareExtractions` from ops-extraction-test into a reusable, unit-
// tested scorer. See docs/plans/2026-09-11-prompt-eval-system-plan.md §6.
//
// Rules: all fields weighted EQUALLY; no pass/fail gate (callers report accuracy +
// deltas); `summary`/`actionItems` (free text) are NOT scored. Free-text person fields
// (service requested, reason not scheduled) score on whether they are present, not on
// wording; insurance and referral match when every expected word appears; relationships
// compare by family role ("dad" = "father"). `callType` scores only when the golden sets
// it: production never saves it, so goldens seeded from a saved call have none.

import type { GoldenExtraction, GoldenPerson } from 'app/components/ops-datasets/mockData'

/** A candidate extraction has the same shape as a golden one. */
export type Extraction = GoldenExtraction

/** Scored pipeline stages. `validation` is intentionally excluded (free text). */
export type Stage = 'overview' | 'people' | 'appointment' | 'outcome'
export const SCORED_STAGES: Stage[] = [`overview`, `people`, `appointment`, `outcome`]

export type FieldResult = {
  /** Field key, unique within a stage (e.g. `name`, `appointmentDate`). */
  field: string
  /** For per-person fields, which person index (1-based); undefined for globals. */
  person?: number
  matched: boolean
  goldenValue: string
  candidateValue: string
}

export type StageScore = {
  stage: Stage
  matched: number
  total: number
  accuracy: number
  fields: FieldResult[]
}

export type ExtractionScore = {
  overall: number
  matched: number
  total: number
  stages: Record<Stage, StageScore>
  /** Human-readable mismatch lines, for the item diff view. */
  discrepancies: string[]
}

const UNKNOWN = `~~~unknown`

function normName(name?: string): string {
  const value = (name ?? ``).toLowerCase().trim()
  return value === `unknown` || value === `` ? UNKNOWN : value
}

type PersonPair = [candidate: GoldenPerson | undefined, golden: GoldenPerson | undefined]

/**
 * Pairs candidate people with golden people by identity, not list position, so one
 * missing or extra person costs only its own fields instead of shifting every later
 * person onto the wrong answer. Named golden people match by name first; the rest match
 * a remaining candidate by caller flag and patient type, then caller flag, then anyone.
 * Leftovers pair with `undefined`: a golden person the candidate missed, or an extra.
 */
function alignPeople(candidatePeople: GoldenPerson[], goldenPeople: GoldenPerson[]): PersonPair[] {
  const remaining = [...candidatePeople]
  const take = (matches: (person: GoldenPerson) => boolean): GoldenPerson | undefined => {
    const index = remaining.findIndex(matches)
    return index === -1 ? undefined : remaining.splice(index, 1)[0]
  }

  const byName = goldenPeople.map((gold) =>
    normName(gold.name) === UNKNOWN ? undefined : take((person) => normName(person.name) === normName(gold.name)),
  )
  const pairs: PersonPair[] = goldenPeople.map((gold, i) => [
    byName[i] ??
      take((person) => person.isCaller === gold.isCaller && person.patientType === gold.patientType) ??
      take((person) => person.isCaller === gold.isCaller) ??
      take(() => true),
    gold,
  ])
  return [...pairs, ...remaining.map((extra): PersonPair => [extra, undefined])]
}

function show(value: unknown): string {
  if (value === undefined || value === null || value === ``) return `—`
  if (Array.isArray(value)) return value.length ? value.join(`, `) : `—`
  return String(value)
}

/** Per-person fields grouped by the stage that owns them. */
const PERSON_FIELDS: Record<Exclude<Stage, 'overview'>, (keyof GoldenPerson)[]> = {
  people: [`name`, `patientType`, `isCaller`, `relationship`, `phone`, `email`, `birthDate`],
  appointment: [
    `appointmentScheduled`,
    `appointmentRequested`,
    `appointmentDate`,
    `appointmentTime`,
    `treatment`,
    `serviceRequested`,
    `reasonForNotScheduling`,
  ],
  outcome: [`outcomes`, `insuranceProvider`, `referral`],
}

/** Free text whose wording varies run to run: only its presence is scored. */
const PRESENCE_FIELDS = new Set<keyof GoldenPerson>([`serviceRequested`, `reasonForNotScheduling`])

/** Short labels compared case- and punctuation-insensitively (`Son` = `son`). */
const TEXT_FIELDS = new Set<keyof GoldenPerson>([`email`, `treatment`])

/** Relationships compare by family role, so "dad" matches "father" and "wife" matches "spouse". */
const ROLE: Record<string, string> = Object.fromEntries(
  Object.entries({
    parent: [`dad`, `father`, `mom`, `mother`, `parent`, `stepfather`, `stepmother`, `guardian`],
    child: [`son`, `daughter`, `kid`, `child`, `stepson`, `stepdaughter`],
    spouse: [`wife`, `husband`, `spouse`, `partner`],
    sibling: [`brother`, `sister`, `sibling`],
    grandparent: [`grandfather`, `grandmother`, `grandparent`, `grandpa`, `grandma`],
    grandchild: [`grandson`, `granddaughter`, `grandchild`],
  }).flatMap(([role, labels]) => labels.map((label) => [label, role])),
)

/** Names that are listed in varying ways ("Delta Dental, MetLife" or "Delta Dental and
 *  MetLife PPO"): they match when every word of the golden appears in the candidate. */
const WORD_FIELDS = new Set<keyof GoldenPerson>([`insuranceProvider`, `referral`])

const normText = (value: unknown) =>
  String(value ?? ``)
    .toLowerCase()
    .replace(/[^a-z0-9@.]+/g, ` `)
    .replace(/^[ .]+|[ .]+$/g, ``)
    .trim()

const words = (value: unknown) =>
  normText(value)
    .split(` `)
    .filter((word) => word && word !== `and`)

function personFieldMatches(field: keyof GoldenPerson, candidate?: GoldenPerson, golden?: GoldenPerson): boolean {
  // A person present on only one side fails every field (false positive / miss).
  if (!candidate || !golden) return false
  if (field === `name`) return normName(candidate.name) === normName(golden.name)
  if (PRESENCE_FIELDS.has(field)) return Boolean(candidate[field]) === Boolean(golden[field])
  if (TEXT_FIELDS.has(field)) return normText(candidate[field]) === normText(golden[field])
  if (field === `relationship`) {
    const role = (value?: string) => ROLE[normText(value)] ?? normText(value)
    return role(candidate.relationship) === role(golden.relationship)
  }
  if (WORD_FIELDS.has(field)) {
    const expected = words(golden[field])
    const found = new Set(words(candidate[field]))
    return expected.length === 0 ? found.size === 0 : expected.every((word) => found.has(word))
  }
  // The pipeline keeps a phone's last 10 digits.
  if (field === `phone`)
    return (candidate.phone ?? ``).replace(/\D/g, ``).slice(-10) === (golden.phone ?? ``).replace(/\D/g, ``).slice(-10)
  if (field === `outcomes`) {
    const c = [...(candidate.outcomes ?? [])].sort().join(`|`)
    const g = [...(golden.outcomes ?? [])].sort().join(`|`)
    return c === g
  }
  const c = candidate[field]
  const g = golden[field]
  // Normalize empty/undefined so `undefined` and `''` compare equal.
  return (c ?? ``) === (g ?? ``)
}

function personFieldDisplay(field: keyof GoldenPerson, person?: GoldenPerson): string {
  if (!person) return `(missing)`
  return show(person[field])
}

function makeStage(stage: Stage, fields: FieldResult[]): StageScore {
  const matched = fields.filter((f) => f.matched).length
  const total = fields.length
  return { stage, matched, total, accuracy: total ? matched / total : 1, fields }
}

/** Score one candidate extraction against its golden. `scope` limits which stages count:
 *  a run that executed only the People stage is not marked wrong on stages it never ran.
 *  Out-of-scope stages score with no fields, so they add nothing to any total. */
export function scoreExtraction(
  candidate: Extraction,
  golden: Extraction,
  scope: Stage[] = SCORED_STAGES,
): ExtractionScore {
  const discrepancies: string[] = []

  // --- Overview stage: global fields. ---
  const overviewKeys = golden.callType
    ? ([`answerer`, `channel`, `callType`] as const)
    : ([`answerer`, `channel`] as const)
  const overviewFields: FieldResult[] = overviewKeys.map((field) => {
    const matched = (candidate[field] ?? ``) === (golden[field] ?? ``)
    if (!matched && scope.includes(`overview`))
      discrepancies.push(`${field}: "${show(golden[field])}" → "${show(candidate[field])}"`)
    return { field, matched, goldenValue: show(golden[field]), candidateValue: show(candidate[field]) }
  })

  // --- People / appointment / outcome stages: aligned per-person comparison. ---
  const candidatePeople = candidate.people ?? []
  const goldenPeople = golden.people ?? []

  const peopleFields: FieldResult[] = []
  const appointmentFields: FieldResult[] = []
  const outcomeFields: FieldResult[] = []

  // People-count is a people-stage field.
  const countMatched = candidatePeople.length === goldenPeople.length
  if (!countMatched && scope.includes(`people`))
    discrepancies.push(`people count: ${goldenPeople.length} → ${candidatePeople.length}`)
  peopleFields.push({
    field: `peopleCount`,
    matched: countMatched,
    goldenValue: String(goldenPeople.length),
    candidateValue: String(candidatePeople.length),
  })

  const bucketFor: Record<Exclude<Stage, 'overview'>, FieldResult[]> = {
    people: peopleFields,
    appointment: appointmentFields,
    outcome: outcomeFields,
  }

  for (const [i, [cand, gold]] of alignPeople(candidatePeople, goldenPeople).entries()) {
    for (const stage of [`people`, `appointment`, `outcome`] as const) {
      for (const field of PERSON_FIELDS[stage]) {
        const matched = personFieldMatches(field, cand, gold)
        const goldenValue = personFieldDisplay(field, gold)
        const candidateValue = personFieldDisplay(field, cand)
        if (!matched && scope.includes(stage)) {
          discrepancies.push(`person ${i + 1} ${String(field)}: "${goldenValue}" → "${candidateValue}"`)
        }
        bucketFor[stage].push({ field: String(field), person: i + 1, matched, goldenValue, candidateValue })
      }
    }
  }

  const fieldsFor: Record<Stage, FieldResult[]> = {
    overview: overviewFields,
    people: peopleFields,
    appointment: appointmentFields,
    outcome: outcomeFields,
  }
  const stages = Object.fromEntries(
    SCORED_STAGES.map((stage) => [stage, makeStage(stage, scope.includes(stage) ? fieldsFor[stage] : [])]),
  ) as Record<Stage, StageScore>

  const matched = SCORED_STAGES.reduce((sum, stage) => sum + stages[stage].matched, 0)
  const total = SCORED_STAGES.reduce((sum, stage) => sum + stages[stage].total, 0)

  return { overall: total ? matched / total : 1, matched, total, stages, discrepancies }
}

export type StageAggregate = { matched: number; total: number; accuracy: number }
export type FieldAggregate = { stage: Stage; field: string; matched: number; total: number; accuracy: number }

export type ScoreAggregate = {
  overall: number
  matched: number
  total: number
  stages: Record<Stage, StageAggregate>
  /** Per-field accuracy across all items, keyed `${stage}.${field}`. */
  fields: Record<string, FieldAggregate>
}

/** Roll up many per-item scores into overall / per-stage / per-field accuracy. */
export function aggregateScores(scores: ExtractionScore[]): ScoreAggregate {
  const stages = Object.fromEntries(
    SCORED_STAGES.map((stage) => [stage, { matched: 0, total: 0, accuracy: 1 }]),
  ) as Record<Stage, StageAggregate>
  const fields: Record<string, FieldAggregate> = {}

  for (const score of scores) {
    for (const stage of SCORED_STAGES) {
      stages[stage].matched += score.stages[stage].matched
      stages[stage].total += score.stages[stage].total
      for (const field of score.stages[stage].fields) {
        const key = `${stage}.${field.field}`
        const bucket = (fields[key] ??= { stage, field: field.field, matched: 0, total: 0, accuracy: 1 })
        bucket.matched += field.matched ? 1 : 0
        bucket.total += 1
      }
    }
  }

  let matched = 0
  let total = 0
  for (const stage of SCORED_STAGES) {
    const bucket = stages[stage]
    bucket.accuracy = bucket.total ? bucket.matched / bucket.total : 1
    matched += bucket.matched
    total += bucket.total
  }
  for (const key of Object.keys(fields)) {
    const bucket = fields[key]
    bucket.accuracy = bucket.total ? bucket.matched / bucket.total : 1
  }

  return { overall: total ? matched / total : 1, matched, total, stages, fields }
}
